const FIELD_KEYS = {
  exam: ["userExam", "exam", "selectedExam"],
  state: ["userState", "state", "selectedState"],
  preferredLanguage: [
    "preferredLanguage",
    "preferred_language",
    "language",
    "lang",
    "userLanguage",
  ],
};

function toCleanString(value) {
  if (value == null) return "";
  return String(value).trim();
}

export function normalizeLanguage(value) {
  const raw = toCleanString(value).toLowerCase();
  if (!raw) return "";
  if (raw === "hi" || raw === "hindi" || raw === "हिन्दी" || raw === "हिंदी") {
    return "hi";
  }
  if (raw === "en" || raw === "english") {
    return "en";
  }
  return raw;
}

export function readProfileField(obj, fieldName) {
  const keys = FIELD_KEYS[fieldName] || [fieldName];
  for (const key of keys) {
    const next = toCleanString(obj?.[key]);
    if (next) return fieldName === "preferredLanguage" ? normalizeLanguage(next) : next;
  }
  return "";
}

export function getUserProfile(obj) {
  return {
    exam: readProfileField(obj, "exam"),
    state: readProfileField(obj, "state"),
    preferredLanguage: readProfileField(obj, "preferredLanguage"),
  };
}

export function buildUserProfilePatch({ exam, state, preferredLanguage }) {
  const normalizedLanguage = normalizeLanguage(preferredLanguage);
  const cleanExam = toCleanString(exam);
  const cleanState = toCleanString(state);

  return {
    exam: cleanExam,
    userExam: cleanExam,
    state: cleanState,
    userState: cleanState,
    preferredLanguage: normalizedLanguage,
    preferred_language: normalizedLanguage,
    language: normalizedLanguage,
    lang: normalizedLanguage,
  };
}

function toArray(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    return value
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return [];
}

function parseStateList(value) {
  const fromArray = toArray(value);
  if (fromArray.length > 0) return fromArray;

  if (value && typeof value === "object" && !Array.isArray(value)) {
    return Object.entries(value)
      .filter(([key, enabled]) => {
        const cleanKey = toCleanString(key);
        if (!cleanKey) return false;
        return enabled === true;
      })
      .map(([key]) => toCleanString(key))
      .filter(Boolean);
  }

  return [];
}

function normalizedValue(value) {
  return toCleanString(value).toLowerCase();
}

function categoryValues(categoryNode, aliases, normalizer = normalizedValue) {
  for (const key of aliases) {
    const raw = categoryNode?.[key];
    const parsed =
      key.toLowerCase().includes("state")
        ? parseStateList(raw)
        : toArray(raw).length > 0
          ? toArray(raw)
          : raw != null
            ? [toCleanString(raw)]
            : [];

    const values = parsed.map((v) => normalizer(v)).filter(Boolean);
    if (values.length > 0) return values;
  }
  return [];
}

function matchesWithAll(values, profileValue, normalizer = normalizedValue) {
  if (values.length === 0) return true;
  if (values.includes("all")) return true;
  const target = normalizer(profileValue);
  if (!target) return false;
  return values.includes(target);
}

function categoryMatchesProfile(categoryNode, profile) {
  const stateValues = categoryValues(categoryNode, [
    "state",
    "states",
    "stateName",
    "state_value",
  ]);
  if (!matchesWithAll(stateValues, profile?.state)) return false;

  const examValues = categoryValues(categoryNode, ["exam", "exams", "targetExam"]);
  if (!matchesWithAll(examValues, profile?.exam)) return false;

  const languageValues = categoryValues(
    categoryNode,
    ["language", "languages", "preferredLanguage", "preferred_language", "lang"],
    normalizeLanguage
  );
  if (!matchesWithAll(languageValues, profile?.preferredLanguage, normalizeLanguage)) {
    return false;
  }

  return true;
}

export function subjectMatchesProfile(subjectNode, profile) {
  const categoriesObj = getSubjectCategories(subjectNode);
  const filtered = filterCategoriesByProfile(categoriesObj, profile);
  return Object.keys(filtered).length > 0;
}

export function filterCategoriesByProfile(categoriesObj, profile) {
  return Object.entries(categoriesObj || {}).reduce((acc, [id, category]) => {
    if (categoryMatchesProfile(category, profile)) {
      acc[id] = category;
    }
    return acc;
  }, {});
}

export function getSubjectCategories(subjectNode) {
  const source =
    subjectNode && typeof subjectNode === "object" && subjectNode.categories
      ? subjectNode.categories
      : subjectNode || {};

  return Object.entries(source || {})
    .filter(([, value]) => value && typeof value === "object" && ("name" in value || "sets" in value))
    .reduce((acc, [key, value]) => {
      acc[key] = value;
      return acc;
    }, {});
}
